import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';

const APP_HOSTS = ['app.sparklayer.io', 'app.dev.sparklayer.io'];
// The `applicationKey` given to the Sentry Vite plugin
const APP_KEY = 'spark-dash';
const MIXPANEL_TOKEN = 'ab795b7a2dc0a7dd5e60bc733715efb5';
const EXTENSION_URL = /(chrome|moz|safari(-web)?)-extension:\/\//;

// Extensions reject on our page with plain objects, which carry no frames for
// `thirdPartyErrorFilterIntegration` to check. 4900 is the EIP-1193 wallet disconnect code.
function isExtensionRejection(reason: unknown): boolean {
  if (typeof reason !== 'object' || reason === null || reason instanceof Error) {
    return false;
  }
  const { code, stack } = reason as { code?: unknown; stack?: unknown };
  return code === 4900 || (typeof stack === 'string' && EXTENSION_URL.test(stack));
}

// Sentry's wrappers are bundled with our code, so an error from an ad or analytics script keeps
// one spark-dash frame and gets past `thirdPartyErrorFilterIntegration`: the outermost frame when
// it's thrown in a callback Sentry wraps, the innermost when it's a failed fetch. Drop the error
// when none of the other frames are ours.
function isThirdPartyError(event: Sentry.ErrorEvent): boolean {
  const exception = event.exception?.values?.at(-1);
  let frames = (exception?.stacktrace?.frames ?? []).filter(
    (frame) => frame.filename && (frame.lineno !== undefined || frame.colno !== undefined),
  );
  if (exception?.mechanism?.type.startsWith('auto.browser.browserapierrors')) {
    frames = frames.slice(1);
  }
  if (exception?.type === 'TypeError' && exception.value?.startsWith('Failed to fetch')) {
    frames = frames.slice(0, -1);
  }
  return (
    frames.length > 0 &&
    frames.every((frame) => !frame.module_metadata?.[`_sentryBundlerPluginAppKey:${APP_KEY}`])
  );
}

export function initAnalytics(router: object) {
  if (!APP_HOSTS.includes(window.location.host)) {
    return;
  }

  Sentry.init({
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.data?.url?.includes('clarity.ms')) {
        return null;
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      return isExtensionRejection(hint.originalException) || isThirdPartyError(event)
        ? null
        : event;
    },
    dsn: 'https://b2cd1b9778ed4f8abe25362246e2c35f@o932531.ingest.sentry.io/5881317',
    environment: window.location.host,
    ignoreErrors: [
      // Ignore network errors which are likely caused by user's unreliable network connection
      'error loading dynamically imported module',
      'Failed to fetch dynamically imported module',
      'Load failed',
      'NetworkError',
      'Network Error',
      'Unable to preload CSS',

      // https://stackoverflow.com/a/50387233
      'ResizeObserver loop limit exceeded',

      'ResizeObserver loop completed with undelivered notifications.',
      'Request failed with status code 401',
      'Request failed with status code 419',

      // Rejections from third-party scripts and extensions, nothing of ours rejects with these
      "You don't have permissions. Reference number",
      'Object captured as promise rejection with keys: [object has no keys]',
      // A script dispatching its own `unhandledrejection` event, which carries no stack
      'Event `CustomEvent` (type=unhandledrejection) captured as promise rejection',
    ],
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: [APP_KEY],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    ],
    // __SENTRY_RELEASE__ is statically replaced at build time by Vite
    // biome-ignore lint/correctness/noUndeclaredVariables: defined in build process
    release: __SENTRY_RELEASE__,
    tracesSampleRate: 0.1,
    sendDefaultPii: true,
  });

  mixpanel.init(MIXPANEL_TOKEN, { track_pageview: false });
}
